import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  Package, Plus, AlertTriangle, TrendingDown, TrendingUp,
  ArrowDownCircle, ArrowUpCircle, History, Calendar, X
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function StockPage() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("crear_pacientes");
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    medication_name: "", current_units: 30, unit_type: "comprimido",
    alert_threshold: 10, blister_size: 10, lot_number: "", expiry_date: ""
  });
  const [movementDialog, setMovementDialog] = useState(null);
  const [movementForm, setMovementForm] = useState({ movement_type: "ingreso", quantity: 10, reason: "", lot_number: "" });
  const [historyDialog, setHistoryDialog] = useState(null);
  const [movements, setMovements] = useState([]);

  const fetchStock = useCallback(async () => {
    try { const r = await api.get("/stock"); setStock(r.data); } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStock(); }, [fetchStock]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        current_units: parseInt(form.current_units),
        alert_threshold: parseInt(form.alert_threshold),
        blister_size: parseInt(form.blister_size)
      };
      if (!payload.lot_number) delete payload.lot_number;
      if (!payload.expiry_date) delete payload.expiry_date;
      await api.post("/stock", payload);
      toast.success("Medicamento agregado al stock");
      setDialogOpen(false);
      setForm({ medication_name: "", current_units: 30, unit_type: "comprimido", alert_threshold: 10, blister_size: 10, lot_number: "", expiry_date: "" });
      fetchStock();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    if (!movementDialog) return;
    try {
      const payload = { ...movementForm, quantity: parseInt(movementForm.quantity) };
      if (!payload.lot_number) delete payload.lot_number;
      if (!payload.reason) delete payload.reason;
      await api.post(`/stock/${movementDialog.id}/movement`, payload);
      toast.success(movementForm.movement_type === "ingreso" ? "Ingreso registrado" : "Egreso registrado");
      setMovementDialog(null);
      setMovementForm({ movement_type: "ingreso", quantity: 10, reason: "", lot_number: "" });
      fetchStock();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openHistory = async (item) => {
    setHistoryDialog(item);
    try {
      const r = await api.get(`/stock/${item.id}/movements`);
      setMovements(r.data);
    } catch { setMovements([]); }
  };

  const getExpiryBadge = (item) => {
    if (!item.expiry_date) return null;
    try {
      const exp = new Date(item.expiry_date);
      const now = new Date();
      const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) return <Badge className="rounded-full bg-rose-500 text-white border-0 text-[10px] font-semibold">VENCIDO</Badge>;
      if (daysLeft <= 30) return <Badge className="rounded-full bg-amber-50 text-amber-700 border-0 text-[10px] font-semibold">Vence en {daysLeft}d</Badge>;
      return null;
    } catch { return null; }
  };

  const lowStockCount = stock.filter(s => s.low_stock).length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Stock de Medicamentos</h1>
          <p className="text-sm text-slate-400 mt-0.5">{stock.length} medicamentos registrados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canManage && (
            <DialogTrigger asChild>
              <Button className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold" data-testid="add-stock-btn">
                <Plus className="w-4 h-4 mr-2" /> Agregar Medicamento
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="rounded-2xl sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Agregar al Stock</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">Registrar nuevo medicamento en el inventario</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="font-medium text-sm text-slate-700">Medicamento</Label>
                <Input value={form.medication_name} onChange={e => setForm({ ...form, medication_name: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="stock-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Unidades</Label>
                  <Input type="number" value={form.current_units} onChange={e => setForm({ ...form, current_units: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="stock-units-input" />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                  <Select value={form.unit_type} onValueChange={v => setForm({ ...form, unit_type: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="stock-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comprimido">Comprimido</SelectItem>
                      <SelectItem value="gota">Gota</SelectItem>
                      <SelectItem value="ml">ML</SelectItem>
                      <SelectItem value="ampolla">Ampolla</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Alerta mínima</Label>
                  <Input type="number" value={form.alert_threshold} onChange={e => setForm({ ...form, alert_threshold: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="stock-threshold-input" />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Blíster (uds)</Label>
                  <Input type="number" value={form.blister_size} onChange={e => setForm({ ...form, blister_size: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="stock-blister-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">N° Lote</Label>
                  <Input value={form.lot_number} onChange={e => setForm({ ...form, lot_number: e.target.value })} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="stock-lot-input" />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Vencimiento</Label>
                  <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="stock-expiry-input" />
                </div>
              </div>
              <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-stock-btn">Agregar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lowStockCount > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-center gap-4" data-testid="low-stock-alert">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-rose-600 animate-pulse-soft" strokeWidth={1.8} />
          </div>
          <div>
            <p className="font-semibold text-sm text-rose-700">{lowStockCount} medicamento(s) con stock bajo</p>
            <p className="text-sm text-rose-500">Requieren reposición urgente</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando stock...</p>
      ) : stock.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-400 font-medium">No hay medicamentos en stock</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stock.map((item) => {
            const maxUnits = Math.max(item.current_units, item.blister_size * 3, item.alert_threshold * 5);
            const pct = Math.min(100, (item.current_units / maxUnits) * 100);
            const expiryBadge = getExpiryBadge(item);
            return (
              <Card
                key={item.id}
                className={`rounded-2xl transition-all duration-200 hover:-translate-y-[1px] ${
                  item.low_stock ? "border border-rose-200 bg-rose-50/30 shadow-sm" : "border-slate-100 shadow-sm hover:shadow-md"
                }`}
                data-testid={`stock-item-${item.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{item.medication_name}</h3>
                      <p className="text-xs text-slate-400">{item.unit_type}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {expiryBadge}
                      {item.low_stock && (
                        <Badge className="rounded-full bg-rose-500 text-white border-0 text-[10px] font-semibold animate-pulse-soft">
                          <TrendingDown className="w-3 h-3 mr-1" /> Bajo
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-3xl font-bold text-slate-900">{item.current_units}</p>
                    <p className="text-xs text-slate-400">unidades disponibles</p>
                  </div>
                  <Progress value={pct} className={`h-1.5 rounded-full ${item.low_stock ? "[&>div]:bg-rose-400" : "[&>div]:bg-emerald-400"}`} />
                  <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
                    <span>Alerta: {item.alert_threshold} uds</span>
                    {item.lot_number && <span>Lote: {item.lot_number}</span>}
                    {item.expiry_date && <span>Venc: {item.expiry_date}</span>}
                  </div>
                  {canManage && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => { setMovementDialog(item); setMovementForm({ movement_type: "ingreso", quantity: item.blister_size || 10, reason: "", lot_number: "" }); }}
                        variant="outline"
                        className="flex-1 rounded-xl border-slate-200 text-slate-600 font-medium hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 transition-all text-sm h-9"
                        data-testid={`movement-btn-${item.id}`}
                      >
                        <ArrowUpCircle className="w-4 h-4 mr-1" /> Movimiento
                      </Button>
                      <Button
                        onClick={() => openHistory(item)}
                        variant="ghost"
                        className="rounded-xl text-slate-400 hover:text-slate-700 h-9 px-3"
                        data-testid={`history-btn-${item.id}`}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Movement Dialog */}
      <Dialog open={!!movementDialog} onOpenChange={() => setMovementDialog(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Registrar Movimiento</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {movementDialog?.medication_name} — Stock actual: {movementDialog?.current_units}
            </DialogDescription>
          </DialogHeader>
          {movementDialog && (
            <form onSubmit={handleMovement} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                <Select value={movementForm.movement_type} onValueChange={v => setMovementForm({ ...movementForm, movement_type: v })}>
                  <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="movement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingreso">Ingreso (agregar)</SelectItem>
                    <SelectItem value="egreso">Egreso (retirar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Cantidad</Label>
                  <Input type="number" value={movementForm.quantity} onChange={e => setMovementForm({ ...movementForm, quantity: e.target.value })} min="1" className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="movement-quantity-input" />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">N° Lote</Label>
                  <Input value={movementForm.lot_number} onChange={e => setMovementForm({ ...movementForm, lot_number: e.target.value })} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="movement-lot-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm text-slate-700">Motivo</Label>
                <Input value={movementForm.reason} onChange={e => setMovementForm({ ...movementForm, reason: e.target.value })} placeholder="Ej: Reposición, Descarte por vencimiento..." className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="movement-reason-input" />
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-400">Stock resultante:</p>
                <p className="text-lg font-bold text-slate-900">
                  {movementForm.movement_type === "ingreso"
                    ? movementDialog.current_units + parseInt(movementForm.quantity || 0)
                    : movementDialog.current_units - parseInt(movementForm.quantity || 0)
                  } unidades
                </p>
              </div>
              <Button
                type="submit"
                className={`w-full rounded-xl h-10 font-semibold active:scale-[0.98] transition-all ${
                  movementForm.movement_type === "ingreso"
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-rose-600 text-white hover:bg-rose-700"
                }`}
                data-testid="submit-movement-btn"
              >
                {movementForm.movement_type === "ingreso" ? "Registrar Ingreso" : "Registrar Egreso"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="rounded-2xl sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 font-[Manrope]">
              Historial — {historyDialog?.medication_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Movimientos de stock registrados</DialogDescription>
          </DialogHeader>
          {movements.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Sin movimientos registrados</p>
          ) : (
            <div className="space-y-2 mt-2">
              {movements.map(m => (
                <div key={m.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3" data-testid={`movement-${m.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    m.movement_type === "ingreso" ? "bg-emerald-50" : "bg-rose-50"
                  }`}>
                    {m.movement_type === "ingreso"
                      ? <ArrowDownCircle className="w-4 h-4 text-emerald-600" />
                      : <ArrowUpCircle className="w-4 h-4 text-rose-600" />
                    }
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${m.movement_type === "ingreso" ? "text-emerald-700" : "text-rose-600"}`}>
                        {m.movement_type === "ingreso" ? "+" : "-"}{m.quantity}
                      </span>
                      {m.lot_number && <span className="text-xs text-slate-400">Lote: {m.lot_number}</span>}
                      {m.balance_after !== undefined && <span className="text-xs text-slate-400">Saldo: {m.balance_after}</span>}
                    </div>
                    {m.reason && <p className="text-xs text-slate-500">{m.reason}</p>}
                    <p className="text-[10px] text-slate-400">{m.created_by_name} — {new Date(m.created_at).toLocaleString("es-AR")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
